import { FC, useEffect, useState, useRef, useCallback } from "react";
import { styled, Alert, Button } from "@monolith-forensics/monolith-ui";
import {
  ImportDefinition,
  ImportRowValidation,
  ImportBatchResult,
} from "../DataImport.types";
import { getImportableRows } from "../utils/getImportableRows";
import { CheckCircle, XCircle, Download, XIcon } from "lucide-react";

const DEFAULT_BATCH_SIZE = 5;

interface ImportProgressStepProps {
  definition: ImportDefinition;
  validatedRows: ImportRowValidation[];
  selectedRows: Set<number>;
  onComplete: () => void;
  onClose?: () => void;
}

interface ImportProgress {
  totalRecords: number;
  totalBatches: number;
  currentBatch: number;
  imported: number;
  failed: number;
  errors: { row: number; message: string }[];
  isComplete: boolean;
  isRunning: boolean;
}

const ImportProgressStep: FC<ImportProgressStepProps> = ({
  definition,
  validatedRows,
  selectedRows,
  onComplete,
  onClose,
}) => {
  const hasStarted = useRef(false);

  const [progress, setProgress] = useState<ImportProgress>(() => {
    const selectedValidRows = getImportableRows(validatedRows, selectedRows);
    const batchSize = definition.batchSize || DEFAULT_BATCH_SIZE;
    return {
      totalRecords: selectedValidRows.length,
      totalBatches: Math.ceil(selectedValidRows.length / batchSize),
      currentBatch: 0,
      imported: 0,
      failed: 0,
      errors: [],
      isComplete: false,
      isRunning: false,
    };
  });

  const runImport = useCallback(async () => {
    const batchSize = definition.batchSize || DEFAULT_BATCH_SIZE;
    const selectedValidRows = getImportableRows(validatedRows, selectedRows);

    const records = selectedValidRows.map((r) => r.data);
    // Parallel array of original CSV rowIndexes so we can attribute per-row
    // errors back to the row the user saw in the Preview step (rather than
    // an index into the selected/valid subset).
    const originalRowIndexes = selectedValidRows.map((r) => r.rowIndex);

    setProgress((prev) => ({ ...prev, isRunning: true }));

    let totalImported = 0;
    let totalFailed = 0;
    const allErrors: { row: number; message: string }[] = [];

    for (let i = 0; i < records.length; i += batchSize) {
      const batch = records.slice(i, i + batchSize);
      const batchIndex = Math.floor(i / batchSize);

      try {
        const result: ImportBatchResult = await definition.onImport(batch);
        totalImported += result.successful;
        totalFailed += result.failed;
        if (result.errors) {
          // ImportBatchResult.errors[].row is batch-relative (0..batch.length-1).
          // Translate via originalRowIndexes so allErrors[].row is the original CSV row.
          allErrors.push(
            ...result.errors.map((e) => ({
              row: originalRowIndexes[i + e.row],
              message: e.message,
            }))
          );
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Batch import failed unexpectedly";
        totalFailed += batch.length;
        for (let j = 0; j < batch.length; j++) {
          allErrors.push({
            row: originalRowIndexes[i + j],
            message: errorMessage,
          });
        }
      }

      setProgress((prev) => ({
        ...prev,
        currentBatch: batchIndex + 1,
        imported: totalImported,
        failed: totalFailed,
        errors: [...allErrors],
      }));
    }

    setProgress((prev) => ({
      ...prev,
      isComplete: true,
      isRunning: false,
    }));

    onComplete();
  }, [definition, onComplete, validatedRows, selectedRows]);

  const handleDownloadReport = useCallback(async () => {
    const PapaMod = await import("papaparse");
    const Papa = PapaMod.default || PapaMod;

    const selectedValidRows = getImportableRows(validatedRows, selectedRows);

    // progress.errors[].row is the original CSV rowIndex, so the lookup
    // and the report rows must both key on rowIndex (not subset position).
    const errorMap = new Map<number, string>();
    progress.errors.forEach((e) => errorMap.set(e.row, e.message));

    const enabledFields = definition.fields.filter((f) => f.enabled !== false);

    const rows = selectedValidRows.map((r) => {
      const failed = errorMap.has(r.rowIndex);
      const row: Record<string, unknown> = {};

      for (const field of enabledFields) {
        row[field.label] = r.data[field.key] ?? "";
      }

      row["Import Status"] = failed ? "Failed" : "Success";
      row["Error Message"] = failed ? errorMap.get(r.rowIndex) : "";

      return row;
    });

    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${definition.id}-import-report.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [definition, validatedRows, selectedRows, progress.errors]);

  useEffect(() => {
    if (!hasStarted.current) {
      hasStarted.current = true;
      runImport();
    }
  }, [runImport]);

  const percentComplete =
    progress.totalBatches > 0
      ? Math.round((progress.currentBatch / progress.totalBatches) * 100)
      : 0;

  return (
    <Container>
      {!progress.isComplete ? (
        <>
          <StatusTitle>Importing Records...</StatusTitle>
          <ProgressBarWrapper>
            <ProgressBarTrack>
              <ProgressBarFill $percent={percentComplete} />
            </ProgressBarTrack>
            <ProgressLabel>{percentComplete}%</ProgressLabel>
          </ProgressBarWrapper>
          <ProgressDetails>
            <span>
              Batch {progress.currentBatch} of {progress.totalBatches}
            </span>
            <span>
              {progress.imported + progress.failed} of {progress.totalRecords}{" "}
              records processed
            </span>
          </ProgressDetails>
        </>
      ) : (
        <>
          <StatusTitle>Import Complete</StatusTitle>
          <ResultsSummary>
            <ResultItem $variant="success">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CheckCircle size={20} />
                <ResultCount>{progress.imported}</ResultCount>
              </div>

              <ResultLabel>Records Imported Successfully</ResultLabel>
            </ResultItem>
            {progress.failed > 0 && (
              <ResultItem $variant="error">
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <XCircle size={20} />
                  <ResultCount>{progress.failed}</ResultCount>
                </div>
                <ResultLabel>Records Failed to Import</ResultLabel>
              </ResultItem>
            )}
          </ResultsSummary>

          <DownloadReportWrapper>
            <Button
              size="xs"
              variant="contained"
              onClick={handleDownloadReport}
              leftSection={<Download size={14} />}
            >
              Download Import Report
            </Button>
            {onClose && (
              <Button
                size="xs"
                color="primary"
                variant="contained"
                leftSection={<XIcon size={14} />}
                onClick={() => {
                  onClose?.();
                }}
              >
                Close Import
              </Button>
            )}
          </DownloadReportWrapper>

          {progress.errors.length > 0 && (
            <ErrorSection>
              <Alert variant="warning">
                <div style={{ fontWeight: 500, marginBottom: 8 }}>
                  {progress.errors.length} error(s) occurred during import:
                </div>
                <ErrorList>
                  {progress.errors.slice(0, 20).map((err, i) => (
                    <ErrorItem key={i}>
                      Row {err.row + 1}: {err.message}
                    </ErrorItem>
                  ))}
                  {progress.errors.length > 20 && (
                    <ErrorItem>
                      ...and {progress.errors.length - 20} more errors
                    </ErrorItem>
                  )}
                </ErrorList>
              </Alert>
            </ErrorSection>
          )}
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px 0;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  text-align: center;
`;

const ProgressBarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressBarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: ${(props) => props.theme.palette.divider};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${(props) => props.$percent}%;
  background: ${(props) => props.theme.palette.primary.main};
  border-radius: 4px;
  transition: width 0.3s ease;
`;

const ProgressLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  min-width: 40px;
  text-align: right;
`;

const ProgressDetails = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const ResultsSummary = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  padding: 16px 0;
`;

const ResultItem = styled.div<{ $variant: "success" | "error" }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: ${(props) => (props.$variant === "success" ? "#4caf50" : "#f44336")};
`;

const ResultCount = styled.div`
  font-size: 28px;
  font-weight: 700;
`;

const ResultLabel = styled.div`
  font-size: 16px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const DownloadReportWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const ErrorSection = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 16px;
  font-size: 13px;
`;

const ErrorItem = styled.li`
  margin-bottom: 4px;
`;

export default ImportProgressStep;
